import React, { useCallback } from 'react'
import { get } from 'lodash-es'
import { FormattedMessage } from 'react-intl'
import { useDispatch } from 'react-redux'
import classnames from 'classnames'

import { TtBadge, TtButton as Button } from '@23/ui-react'

import {
  type ChatId,
  isAudienceChatEnabled,
  setSelectedChat,
  useSelectedChatId,
  useUnreadChatCount,
} from '../../../../../Chat/blocs/chatVisibility'
import { generateActions } from '../../../../utils/bundleFeature'
import { useLiveDataQuery } from '../../../../../global/blocs/useLiveData'
import useFeatures from '../../../../hooks/useFeatures'

import './WebinarChatSelectorComponent.scss'

const { actionNames: chatActionNames } = generateActions(
  'webinar-sidebar-chat',
  ['SET_SHOULD_SCROLL']
)

const { actionNames: speakerChatActionNames } = generateActions(
  'webinar-sidebar-speaker-chat',
  ['SET_SHOULD_SCROLL']
)

const formatUnread = (n: number) => {
  return n > 99 ? '99+' : `${n}`
}

type ChatTabButtonProps = {
  id: ChatId
  isSelected: boolean
  onChange: (id: ChatId) => void
}

const ChatTabButton = ({ id, isSelected, onChange }: ChatTabButtonProps) => {
  const unreadCount = useUnreadChatCount(id)

  return (
    <Button
      className={classnames('webinar-chat-selector-button', {
        'webinar-chat-selector-button-selected': isSelected,
      })}
      size="small"
      onClick={() => {
        onChange(id)
      }}
      key={id}
      role="tab"
      aria-selected={isSelected}
    >
      {id === 'speaker-chat' ? (
        <FormattedMessage defaultMessage="Speaker Chat" />
      ) : (
        <FormattedMessage defaultMessage="Audience Chat" />
      )}

      {unreadCount > 0 && (
        <TtBadge slot="suffix" pill>
          {formatUnread(unreadCount)}
        </TtBadge>
      )}
    </Button>
  )
}

const WebinarChatSelectorComponent = () => {
  const dispatch = useDispatch()
  const selectedChatId = useSelectedChatId()
  const { data: liveData } = useLiveDataQuery()
  const { HasSpeakerChatEnabled } = useFeatures()

  const audienceChatEnabled = isAudienceChatEnabled(
    get(liveData, 'comment_mode', '')
  )

  const onChange = useCallback(
    (id: ChatId) => {
      setSelectedChat(id)
      switch (id) {
        case 'chat':
          dispatch({
            // @ts-expect-error — legacy javascript; not properly typed
            type: chatActionNames.SET_SHOULD_SCROLL,
            shouldScroll: true,
          })
          break
        case 'speaker-chat':
          dispatch({
            // @ts-expect-error — legacy javascript; not properly typed
            type: speakerChatActionNames.SET_SHOULD_SCROLL,
            shouldScroll: true,
          })
          break
        default:
      }
    },
    [dispatch]
  )

  const tabsToShow: ChatId[] = []
  if (audienceChatEnabled) {
    tabsToShow.push('chat')
  }
  if (HasSpeakerChatEnabled) {
    tabsToShow.push('speaker-chat')
  }

  return (
    <div className="webinar-chat-selector-component">
      {tabsToShow.map((id) => (
        <ChatTabButton
          key={id}
          id={id}
          isSelected={selectedChatId === id}
          onChange={onChange}
        />
      ))}
    </div>
  )
}

export { WebinarChatSelectorComponent }
